<template>
  <div class="marketplace-card" :data-cy="cySelector">
    <Card :class="{ hasErrors: !!errorText }">
      <template #title>
        <div class="full-width">
          <div class="d-flex pb-1 mb-2">
            <slot name="logo">
              <img :src="logoPath" :alt="logoAlt" class="mr-auto logo-img" />
            </slot>
            <ToggleSwitch
              v-if="!hideToggle"
              v-model="enabled"
              data-cy="enabledSwitch"
              :disabled="disabled"
            />
          </div>
          <div class="description">
            {{ description }}
            <Alert
              v-if="!!errorText"
              class="mt-3"
              type="error"
              :text="errorText"
            ></Alert>
            <Alert
              v-if="!!noticeText"
              class="mt-3"
              :type="noticeType"
              :text="noticeText"
            ></Alert>
          </div>
        </div>
      </template>
      <template #body>
        <div>
          <div
            class="d-flex flex-column justify-content-center mb-2 card-actions"
          >
            <div
              class="d-flex align-items-center justify-content-between flex-wrap"
            >
              <div class="configure-label">
                <slot name="configure"></slot>
              </div>
              <div v-if="learnMoreLink" class="learn-more-label">
                <sera-button
                  data-cy="learn-more-link"
                  ghost
                  :label="learnMoreText"
                  @click="handleLearnMoreButtonClick"
                />
              </div>
            </div>
            <slot name="modal" />
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import Card from "@/components/Card.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";

export default {
  components: {
    Card,
    ToggleSwitch,
    Alert
  },
  props: {
    hideToggle: {
      type: Boolean,
      default: false
    },
    logoPath: {
      type: String,
      default: null
    },
    logoAlt: {
      type: String,
      default: "Logo"
    },
    description: {
      type: String,
      default: "Integration details"
    },
    errorText: {
      type: String,
      default: ""
    },
    noticeText: {
      type: String,
      default: ""
    },
    noticeType: {
      type: String,
      default: "warning"
    },
    modelValue: {
      type: Boolean,
      default: false
    },
    learnMoreLink: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    cySelector: {
      type: String,
      default: ""
    },
    learnMoreText: {
      type: String,
      default: "Learn More"
    }
  },
  emits: ["update:modelValue"],
  computed: {
    enabled: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      }
    }
  },
  methods: {
    handleLearnMoreButtonClick() {
      window.open(this.learnMoreLink);
    }
  }
};
</script>

<style lang="scss" scoped>
.marketplace-card {
  width: 355px;
  margin: 0 30px 30px 0;
  font-size: 1.1em;

  .description {
    // A minimum, not a fixed height: the cards line up as a grid, but a card that
    // also renders a notice or error alert in here can grow to hold it instead of
    // clipping it or pushing its own action row out of line with its neighbours.
    min-height: 90px;
    text-overflow: ellipsis;
    color: var(--font-body-color);
  }

  .hasErrors {
    border: 1px solid var(--sera-color-text-error);
  }

  // Card puts the description in .card-header and the actions in .card-body, each
  // with its own padding. On a card whose description does not fill the 90px that
  // padding is invisible, but under an alert it reads as a gap between the message
  // and the buttons. Closed up here rather than in Card, which other pages use.
  :deep(.card-header) {
    padding-bottom: 0.25rem;
  }

  :deep(.card-body) {
    padding-top: 0.5rem;
  }
}

label {
  font-weight: normal !important;
}

.configure-label {
  margin-bottom: 0;
}

.logo-img {
  height: 43px;
}

.toggle-button {
  width: 200px;
}

.card-actions {
  background-color: var(--light-gray);
  border-radius: 6px;
  padding: 6px 17px;
  min-height: 60px;
}

:deep(.card) {
  height: 100%;
  box-shadow: none;
}
</style>
